// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color } from "@werner/XDL/Utilities";
import Color from "color";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    enableGrayscaleOnHover = "enableGrayscaleOnHover",
    url = "url",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.openInNewTab]?: boolean;
        [fields.url]: LinkFieldValue;
        [fields.enableGrayscaleOnHover]?: boolean;
    };
}

type Props = OwnProps;

export interface BrandDetailsHoverImageStyles {
    container?: ClickablePresentationProps;
    imageWrapper?: InjectableCss;
    image?: ImgProps;
}

export const brandDetailsHoverImageStyles: BrandDetailsHoverImageStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    imageWrapper: {
        css: css`
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
        `,
    },
    image: {
        css: css`
            ${({ enableGrayscale }: any) =>
                enableGrayscale &&
                css`
                    &:hover {
                        filter: grayscale(100%);
                    }
                `}
        `,
    },
};

const styles = brandDetailsHoverImageStyles;
const BrandDetailsHoverImage: FC<Props> = ({ fields }) => {
    const link = useGetLink(fields.url);

    return (
        <Clickable
            {...styles.container}
            href={link.url || ""}
            target={fields.openInNewTab ? "_blank" : ""}
            data-test-selector="brand-details-image-widget"
            aria-label="Brand Details Image Clickable"
        >
            <StyledWrapper {...styles.imageWrapper}>
                <Img src={fields.image} {...styles.image} {...{ enableGrayscale: fields.enableGrayscaleOnHover }} altText="Brand Details Image" />
            </StyledWrapper>
        </Clickable>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsHoverImage,
    definition: {
        group: "Brand Details",
        displayName: "Brand Details Image",
        allowedContexts: ["BrandDetailsPage"],
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.enableGrayscaleOnHover,
                displayName: "Enable Grayscale Effect on Hover",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                isRequired: true,
                fieldType: "General",
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
